import type { Project, ExportOptions, Chapter, SectionType } from '../types';

const FRONT_MATTER_SECTIONS: SectionType[] = [
  'half-title', 'title-page', 'copyright', 'dedication', 'epigraph',
  'toc', 'foreword', 'preface', 'introduction',
];

const BACK_MATTER_SECTIONS: SectionType[] = [
  'afterword', 'acknowledgments', 'appendix', 'glossary',
  'bibliography', 'notes', 'about-author', 'also-by',
];

/** Light sanitize: strip <script> and on* attributes from Tiptap HTML */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');
}

function buildCss(options: ExportOptions): string {
  const fontFamily =
    options.fontFamily === 'serif'
      ? 'Georgia, "Times New Roman", serif'
      : 'Helvetica, Arial, sans-serif';

  const baseFontSize =
    options.fontSize === 'small' ? '16px' : options.fontSize === 'large' ? '20px' : '18px';

  const lineHeight =
    options.lineSpacing === 'single' ? '1.4' : options.lineSpacing === 'double' ? '2.0' : '1.7';

  const maxWidth =
    options.margins === 'narrow' ? '800px' : options.margins === 'wide' ? '580px' : '680px';

  return `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --font: ${fontFamily};
      --size: ${baseFontSize};
      --lh: ${lineHeight};
      --max: ${maxWidth};
      --fg: #1a1a1a;
      --muted: #555;
      --accent: #2c3e50;
      --border: #d4c9b8;
      --bg: #faf8f5;
      --page-bg: #fff;
    }

    html { background: var(--bg); }

    body {
      font-family: var(--font);
      font-size: var(--size);
      line-height: var(--lh);
      color: var(--fg);
      background: var(--bg);
      padding: 2rem 1rem 4rem;
    }

    .book-wrapper {
      max-width: var(--max);
      margin: 0 auto;
      background: var(--page-bg);
      box-shadow: 0 4px 32px rgba(0,0,0,0.10);
      border-radius: 4px;
      overflow: hidden;
    }

    /* ── Cover / Title ── */
    .book-cover {
      background: linear-gradient(160deg, var(--accent) 0%, #1a252f 100%);
      color: #fff;
      padding: 5rem 3rem 4rem;
      text-align: center;
    }
    .book-cover img {
      max-width: 240px;
      border-radius: 4px;
      margin-bottom: 2rem;
      box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    }
    .book-cover h1 {
      font-size: 2.4em;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin-bottom: 0.4rem;
      color: #fff;
    }
    .book-cover .subtitle {
      font-size: 1.15em;
      font-style: italic;
      opacity: 0.8;
      margin-bottom: 1.5rem;
    }
    .book-cover .author {
      font-size: 1em;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      opacity: 0.75;
      margin-top: 1rem;
    }
    .book-cover .genre {
      font-size: 0.8em;
      opacity: 0.55;
      margin-top: 0.5rem;
    }

    /* ── Table of Contents ── */
    .toc-section {
      padding: 3rem 3.5rem;
      border-bottom: 1px solid var(--border);
    }
    .toc-section h2 {
      font-size: 1.1em;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 1.5rem;
    }
    .toc-list {
      list-style: none;
    }
    .toc-list li {
      display: flex;
      align-items: baseline;
      gap: 0;
      padding: 0.35rem 0;
      border-bottom: 1px dotted var(--border);
    }
    .toc-list li:last-child { border-bottom: none; }
    .toc-list a {
      color: var(--fg);
      text-decoration: none;
      font-size: 0.95em;
    }
    .toc-list a:hover { color: var(--accent); text-decoration: underline; }
    .toc-num {
      font-size: 0.8em;
      color: var(--muted);
      min-width: 2.2rem;
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    /* ── Chapters ── */
    .chapter {
      padding: 4rem 3.5rem 3rem;
      border-bottom: 1px solid var(--border);
      break-before: page;
    }
    .chapter:last-child { border-bottom: none; }

    .chapter-label {
      font-size: 0.7em;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 0.6rem;
    }
    .chapter-title {
      font-size: 1.8em;
      font-weight: 700;
      color: var(--accent);
      margin-bottom: 2rem;
      line-height: 1.2;
    }
    .chapter-content {
      text-align: justify;
      hyphens: auto;
    }

    /* Tiptap content passthrough */
    .chapter-content p {
      margin-bottom: 0.2em;
      text-indent: 1.6em;
    }
    .chapter-content p:first-child,
    .chapter-content h1 + p,
    .chapter-content h2 + p,
    .chapter-content h3 + p {
      text-indent: 0;
    }
    .chapter-content h1,
    .chapter-content h2,
    .chapter-content h3 {
      font-weight: 700;
      color: var(--accent);
      margin: 1.5em 0 0.4em;
      line-height: 1.25;
    }
    .chapter-content h1 { font-size: 1.4em; }
    .chapter-content h2 { font-size: 1.2em; }
    .chapter-content h3 { font-size: 1.05em; }
    .chapter-content blockquote {
      margin: 1.2em 0 1.2em 2em;
      padding-left: 1em;
      border-left: 3px solid var(--border);
      font-style: italic;
      color: var(--muted);
    }
    .chapter-content ul,
    .chapter-content ol {
      margin: 0.8em 0 0.8em 2em;
    }
    .chapter-content li { margin-bottom: 0.2em; }
    .chapter-content strong { font-weight: 700; }
    .chapter-content em { font-style: italic; }
    .chapter-content hr {
      border: none;
      border-top: 1px solid var(--border);
      margin: 2em auto;
      width: 40%;
    }

    /* ── Front/Back matter ── */
    .front-matter,
    .back-matter {
      padding: 3rem 3.5rem;
      border-bottom: 1px solid var(--border);
    }
    .front-matter:last-child,
    .back-matter:last-child { border-bottom: none; }
    .section-heading {
      font-size: 1.4em;
      font-weight: 700;
      color: var(--accent);
      margin-bottom: 1.5rem;
    }

    /* ── Footer ── */
    .book-footer {
      background: var(--accent);
      color: rgba(255,255,255,0.6);
      text-align: center;
      padding: 1.2rem;
      font-size: 0.75em;
      letter-spacing: 0.05em;
    }

    /* ── Print ── */
    @media print {
      body { background: #fff; padding: 0; }
      .book-wrapper { box-shadow: none; }
      .chapter { break-before: page; }
    }
  `;
}

function buildTocHtml(chapters: Chapter[]): string {
  const SKIP_TYPES: SectionType[] = ['toc', 'half-title', 'title-page', 'copyright', 'epigraph'];
  const visible = chapters.filter((ch) => !SKIP_TYPES.includes(ch.sectionType));

  let chapterNum = 0;
  const items = visible.map((ch) => {
    const isChapter = ch.sectionType === 'chapter';
    if (isChapter) chapterNum++;

    const num = isChapter
      ? `<span class="toc-num">${chapterNum}.</span>`
      : '<span class="toc-num"></span>';

    const label = ch.title || 'Untitled';
    return `<li>${num}<a href="#ch-${ch.id}">${label}</a></li>`;
  });

  return `
    <section class="toc-section" id="toc">
      <h2>Contents</h2>
      <ul class="toc-list">
        ${items.join('\n        ')}
      </ul>
    </section>
  `;
}

function getSectionLabel(ch: Chapter, index: number, allChapters: Chapter[]): string {
  if (ch.sectionType === 'chapter') {
    const num = allChapters
      .filter((c) => c.sectionType === 'chapter')
      .findIndex((c) => c.id === ch.id) + 1;
    return `Chapter ${num}`;
  }

  const labels: Partial<Record<SectionType, string>> = {
    prologue: 'Prologue',
    epilogue: 'Epilogue',
    afterword: 'Afterword',
    foreword: 'Foreword',
    preface: 'Preface',
    introduction: 'Introduction',
    acknowledgments: 'Acknowledgments',
    appendix: 'Appendix',
    glossary: 'Glossary',
    bibliography: 'Bibliography',
    notes: 'Notes',
    'about-author': 'About the Author',
    'also-by': 'Also By',
    interlude: 'Interlude',
    part: 'Part',
    dedication: 'Dedication',
    'half-title': '',
    'title-page': '',
    copyright: 'Copyright',
    epigraph: '',
    toc: 'Contents',
  };
  return labels[ch.sectionType] ?? '';
}

function buildChapterHtml(ch: Chapter, chNum: number, allChapters: Chapter[]): string {
  const label = getSectionLabel(ch, chNum, allChapters);
  const labelHtml = label ? `<p class="chapter-label">${label}</p>` : '';
  const titleHtml =
    ch.title ? `<h2 class="chapter-title">${ch.title}</h2>` : '';
  const content = sanitizeHtml(ch.content || '');

  return `
    <section class="chapter" id="ch-${ch.id}">
      ${labelHtml}
      ${titleHtml}
      <div class="chapter-content">${content}</div>
    </section>
  `;
}

function buildFrontOrBackSection(ch: Chapter, cssClass: string): string {
  const content = sanitizeHtml(ch.content || '');
  const titleHtml = ch.title
    ? `<h2 class="section-heading">${ch.title}</h2>`
    : '';

  return `
    <section class="${cssClass}" id="ch-${ch.id}">
      ${titleHtml}
      <div class="chapter-content">${content}</div>
    </section>
  `;
}

export function exportToHtml(project: Project, options: ExportOptions): Blob {
  const { title, subtitle, author, genre, coverImage, publisher } = project.metadata;

  const css = buildCss(options);

  // Cover section
  const coverImgHtml =
    options.includeCover && coverImage
      ? `<img src="${coverImage}" alt="Book cover" />`
      : '';
  const subtitleHtml = subtitle
    ? `<p class="subtitle">${subtitle}</p>`
    : '';
  const authorHtml = author
    ? `<p class="author">${author}</p>`
    : '';
  const genreHtml = genre
    ? `<p class="genre">${genre}</p>`
    : '';

  const coverSection = `
    <header class="book-cover">
      ${coverImgHtml}
      <h1>${title || 'Untitled'}</h1>
      ${subtitleHtml}
      ${authorHtml}
      ${genreHtml}
    </header>
  `;

  // TOC
  const tocSection =
    options.includeToc ? buildTocHtml(project.chapters) : '';

  // Split chapters by role
  const frontMatter = project.chapters.filter(
    (ch) =>
      FRONT_MATTER_SECTIONS.includes(ch.sectionType) &&
      ch.sectionType !== 'toc' &&
      ch.sectionType !== 'half-title' &&
      !(ch.sectionType === 'title-page' && !options.includeTitle) &&
      !(ch.sectionType === 'copyright' && !options.includeCopyright),
  );

  const body = project.chapters.filter(
    (ch) => !FRONT_MATTER_SECTIONS.includes(ch.sectionType) && !BACK_MATTER_SECTIONS.includes(ch.sectionType),
  );

  const backMatter = project.chapters.filter((ch) =>
    BACK_MATTER_SECTIONS.includes(ch.sectionType),
  );

  const frontHtml = frontMatter
    .map((ch) => buildFrontOrBackSection(ch, 'front-matter'))
    .join('\n');

  const bodyHtml = body
    .map((ch, i) => buildChapterHtml(ch, i, project.chapters))
    .join('\n');

  const backHtml = backMatter
    .map((ch) => buildFrontOrBackSection(ch, 'back-matter'))
    .join('\n');

  const footerHtml = `
    <footer class="book-footer">
      ${title || 'Untitled'}${author ? ` · ${author}` : ''}${publisher ? ` · ${publisher}` : ''}
    </footer>
  `;

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title || 'Untitled'}</title>
  <meta name="author" content="${author || ''}" />
  <style>
${css}
  </style>
</head>
<body>
  <div class="book-wrapper">
    ${coverSection}
    ${tocSection}
    ${frontHtml}
    ${bodyHtml}
    ${backHtml}
    ${footerHtml}
  </div>
</body>
</html>`;

  return new Blob([html], { type: 'text/html' });
}
